"use client";

import React, { useState, useEffect } from "react";
import { FadeIn } from "./FadeIn";

const sections = [
  { id: "intro", number: "1", title: "What is Weaven", parent: null },
  { id: "ownership", number: "2", title: "Data Ownership", parent: null },
  { id: "mechanism", number: "3", title: "How it Works", parent: null },
  { id: "setup", number: "4", title: "Setup your Blog", parent: null },
  { id: "setup-1", number: "4.1", title: "Sign in with Google", parent: "setup" },
  { id: "setup-2", number: "4.2", title: "Choose your blog username", parent: "setup" },
  { id: "setup-3", number: "4.3", title: "Create a Sanity project", parent: "setup" },
  { id: "setup-4", number: "4.4", title: "Verify Sanity credentials", parent: "setup" },
  { id: "setup-5", number: "4.5", title: "Configure CORS in Sanity", parent: "setup" },
  { id: "setup-6", number: "4.6", title: "Enable Comments", parent: "setup" },
  { id: "setup-7", number: "4.7", title: "Create your blog", parent: "setup" },
  { id: "studio", number: "5", title: "Writing in Studio", parent: null },
  { id: "studio-1", number: "5.1", title: "How to Write a Post", parent: "studio" },
  { id: "studio-2", number: "5.2", title: "Setting Up Your About Page", parent: "studio" },
  { id: "studio-3", number: "5.3", title: "Create Author", parent: "studio" },
  { id: "collaborators", number: "6", title: "Inviting Collaborators", parent: null },
];

export function DocsContent() {
  const [activeSection, setActiveSection] = useState<string>("intro");

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-20% 0px -60% 0px",
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100; // Header height + some padding
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="w-full pt-32 pb-32 px-6 md:px-12 bg-paper min-h-screen">
      <div className="max-w-6xl mx-auto">
        <FadeIn>
          <header className="mb-20 border-b border-stone/10 pb-12">
            <span className="block text-xs font-sans font-medium text-stone uppercase tracking-widest mb-6">
              Documentation
            </span>
            <h1 className="font-serif text-5xl md:text-6xl text-ink leading-[1.1] mb-6">
              How to weave.
            </h1>
            <p className="font-sans text-xl text-stone font-light max-w-2xl mb-6">
              Weaven is a view layer, not a storage locker. <br />
              Here is how to set up your studio and own your data.
            </p>
            <p className="font-sans text-base text-stone/70">
              Want to see an example?{" "}
              <a
                href="https://endvchaos.weaven.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink underline underline-offset-4 decoration-stone/30 hover:decoration-ink transition-colors"
              >
                endvchaos.weaven.io
              </a>
            </p>
          </header>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-32 space-y-4">
              <p className="font-sans text-xs font-bold text-ink uppercase tracking-wider mb-4">
                Contents
              </p>
              <nav className="flex flex-col space-y-2 text-sm font-sans">
                {sections.map(({ id, number, title, parent }) => {
                  // 서브섹션인 경우, 현재 활성 섹션이 같은 parent에 속하거나 parent 자체일 때만 표시
                  const activeParent = sections.find(s => s.id === activeSection)?.parent;
                  const isSubsectionVisible = !parent ||
                    activeSection === parent ||
                    activeParent === parent;

                  if (!isSubsectionVisible) return null;

                  return (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className={`text-left transition-all duration-300 flex items-center gap-2 ${
                        parent ? "pl-4 text-xs" : ""
                      } ${
                        activeSection === id
                          ? "text-ink font-medium"
                          : "text-stone/60 hover:text-ink"
                      }`}
                    >
                      <span
                        className={`inline-flex items-center justify-center text-[10px] transition-all duration-300 ${
                          parent ? "w-4 h-4" : "w-5 h-5"
                        } ${
                          activeSection === id
                            ? "text-ink font-bold"
                            : "text-stone/40"
                        }`}
                      >
                        {number}
                      </span>
                      <span className={`${activeSection === id ? "border-b border-ink" : ""}`}>
                        {title}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="lg:col-span-8 lg:col-start-5 space-y-24">
            {/* Section 1 */}
            <section id="intro">
              <FadeIn delay={0.1}>
                <h2 className="font-serif text-3xl text-ink mb-8">
                  1. What is Weaven
                </h2>
                <div className="prose prose-stone font-serif text-lg leading-relaxed text-stone/80">
                  <p className="mb-6">
                    Weaven is not a place where your writing lives. It is a{" "}
                    <strong className="text-ink font-medium">view layer</strong>{" "}
                    that reads content from your own Sanity project and presents
                    it in a calm, minimal way.
                  </p>
                  <p className="mb-6">
                    You write in Sanity. Weaven simply displays your writing.
                    Think of Weaven as:
                  </p>
                  <ul className="list-disc pl-5 mb-6 space-y-2 font-sans text-base">
                    <li>A stage, not a container</li>
                    <li>A lens, not a vault</li>
                    <li>One way your writing can be seen</li>
                  </ul>
                  <p className="italic text-ink">
                    Your content can outlive Weaven. That is intentional.
                  </p>
                </div>
              </FadeIn>
            </section>

            {/* Section 2 */}
            <section id="ownership">
              <FadeIn delay={0.1}>
                <h2 className="font-serif text-3xl text-ink mb-8">
                  2. Data Ownership
                </h2>
                <div className="prose prose-stone font-serif text-lg leading-relaxed text-stone/80">
                  <p className="mb-6">
                    Your writing belongs to you. Your Sanity project is created
                    under your own account, your dataset belongs to you, and
                    billing is handled directly by you.
                  </p>
                  <p className="mb-8">Weaven does not:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 font-sans text-base">
                    <div className="p-4 border border-stone/10 bg-white rounded-sm">
                      <span className="text-red-400 mr-2">×</span> Store your
                      content
                    </div>
                    <div className="p-4 border border-stone/10 bg-white rounded-sm">
                      <span className="text-red-400 mr-2">×</span> Duplicate
                      your data
                    </div>
                    <div className="p-4 border border-stone/10 bg-white rounded-sm">
                      <span className="text-red-400 mr-2">×</span> Restrict
                      exports
                    </div>
                    <div className="p-4 border border-stone/10 bg-white rounded-sm">
                      <span className="text-red-400 mr-2">×</span> Prevent you
                      from leaving
                    </div>
                  </div>
                  <p>
                    If Weaven disappears, your Sanity project and data remain
                    untouched.{" "}
                    <span className="text-ink font-medium">
                      This is not a feature. It is a design decision.
                    </span>
                  </p>
                </div>
              </FadeIn>
            </section>

            {/* Section 3 */}
            <section id="mechanism">
              <FadeIn delay={0.1}>
                <h2 className="font-serif text-3xl text-ink mb-8">
                  3. How Weaven Works
                </h2>
                <div className="prose prose-stone font-serif text-lg leading-relaxed text-stone/80">
                  <p className="mb-6">Weaven connects three things:</p>
                  <ol className="list-decimal pl-5 mb-8 space-y-2 font-sans text-base">
                    <li>Your blog username (subdomain)</li>
                    <li>Your Sanity project and dataset</li>
                    <li>A predefined view and rhythm</li>
                  </ol>
                  <div className="bg-stone/5 p-6 rounded-sm border-l-2 border-ink mb-6">
                    <p className="font-sans text-sm text-ink mb-2 font-bold uppercase tracking-wider">
                      The Flow
                    </p>
                    <p className="font-sans text-sm text-stone leading-relaxed">
                      You create a Project in Sanity &rarr; You connect it to
                      Weaven &rarr; Weaven loads a default schema &rarr; You
                      write &rarr; Weaven reads via API.
                    </p>
                  </div>
                  <p>
                    Weaven never modifies your data. It only reads from Sanity
                    using queries.
                  </p>
                </div>
              </FadeIn>
            </section>

            {/* Section 4 */}
            <section id="setup">
              <FadeIn delay={0.1}>
                <h2 className="font-serif text-3xl text-ink mb-8">
                  4. Setup your Blog
                </h2>
                <div className="space-y-12">
                  {/* Step 1 */}
                  <div id="setup-1">
                    <h3 className="font-sans text-lg font-medium text-ink mb-4 flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ink text-paper text-xs">
                        1
                      </span>
                      Sign in with Google
                    </h3>
                    <p className="font-serif text-stone/80 mb-4 pl-9">
                      Go to{" "}
                      <a
                        href="https://weaven.io"
                        className="text-ink underline decoration-stone/30 underline-offset-4"
                      >
                        weaven.io
                      </a>{" "}
                      and sign in with your Google account.
                    </p>
                  </div>

                  {/* Step 2 */}
                  <div id="setup-2">
                    <h3 className="font-sans text-lg font-medium text-ink mb-4 flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ink text-paper text-xs">
                        2
                      </span>
                      Choose your blog username
                    </h3>
                    <div className="pl-9 space-y-4 font-serif text-stone/80">
                      <p>Your blog username becomes your subdomain.</p>
                      <div className="bg-stone/10 px-3 py-2 rounded text-sm text-ink font-mono inline-block">
                        username.weaven.io
                      </div>
                      <div className="bg-amber-50 border border-amber-100 p-4 rounded-sm">
                        <p className="font-sans text-sm text-amber-900 font-medium mb-2">
                          Important
                        </p>
                        <p className="font-sans text-sm text-amber-800/80">
                          You must click the &quot;Check Availability&quot;
                          button to verify your username is available before
                          proceeding.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div id="setup-3">
                    <h3 className="font-sans text-lg font-medium text-ink mb-4 flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ink text-paper text-xs">
                        3
                      </span>
                      Create a Sanity project
                    </h3>
                    <div className="pl-9 space-y-4 font-serif text-stone/80">
                      <p>
                        Sign in at{" "}
                        <a
                          href="https://sanity.io"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-ink underline decoration-stone/30 underline-offset-4"
                        >
                          sanity.io
                        </a>{" "}
                        and create a new project.
                      </p>
                      <p>
                        By default, Sanity creates a dataset named{" "}
                        <code className="bg-stone/10 px-1 text-sm font-sans">
                          production
                        </code>
                        . You don&apos;t need to create one manually unless you
                        want a custom setup.
                      </p>
                      <div className="bg-white border border-stone/20 p-4 rounded-sm">
                        <p className="text-xs font-sans uppercase text-stone/50 mb-2">
                          You will need:
                        </p>
                        <ul className="list-disc pl-4 space-y-1 font-sans text-sm text-ink">
                          <li>Project ID (e.g., abc12345)</li>
                          <li>Dataset name (usually &apos;production&apos;)</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div id="setup-4">
                    <h3 className="font-sans text-lg font-medium text-ink mb-4 flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ink text-paper text-xs">
                        4
                      </span>
                      Verify Sanity credentials
                    </h3>
                    <div className="pl-9 space-y-4 font-serif text-stone/80">
                      <p>
                        Enter your Project ID and Dataset name in the admin
                        form, then click the &quot;Verify Sanity&quot; button.
                      </p>
                      <p>
                        Weaven will attempt to connect to your Sanity project
                        and confirm that:
                      </p>
                      <ul className="list-disc pl-5 space-y-2 font-sans text-base">
                        <li>The Project ID exists and is accessible</li>
                        <li>The Dataset name is correct</li>
                        <li>Weaven can successfully read from your project</li>
                      </ul>
                      <p>
                        If verification succeeds, you&apos;ll see a green
                        checkmark (✓). If it fails, check that your credentials
                        are correct and that your Sanity project allows public
                        read access.
                      </p>
                      <div className="bg-amber-50 border border-amber-100 p-4 rounded-sm">
                        <p className="font-sans text-sm text-amber-900 font-medium mb-2">
                          Important
                        </p>
                        <p className="font-sans text-sm text-amber-800/80 mb-3">
                          Verification is mandatory. The &quot;Create Blog&quot;
                          button will remain disabled until both username check
                          and Sanity verification are complete.
                        </p>
                        <p className="font-sans text-sm text-amber-800/80">
                          If you change your Project ID or Dataset later, you
                          must verify again before saving.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div id="setup-5">
                    <h3 className="font-sans text-lg font-medium text-ink mb-4 flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ink text-paper text-xs">
                        5
                      </span>
                      Configure CORS in Sanity
                    </h3>
                    <div className="pl-9 space-y-4 font-serif text-stone/80">
                      <p>
                        To allow Weaven to read your content and enable live preview,
                        you must add your blog&apos;s domain to Sanity&apos;s CORS
                        allowed origins.
                      </p>
                      <div className="bg-amber-50 border border-amber-100 p-6 rounded-sm">
                        <h4 className="font-sans font-medium text-amber-900 mb-4">
                          Add CORS Origin
                        </h4>
                        <ol className="list-decimal pl-5 space-y-3 font-sans text-sm text-amber-900">
                          <li>
                            Go to{" "}
                            <a
                              href="https://sanity.io/manage"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-ink underline decoration-amber-300 underline-offset-2"
                            >
                              sanity.io/manage
                            </a>
                          </li>
                          <li>Select your project &rarr; API &rarr; CORS Origins</li>
                          <li>Click &quot;Add CORS origin&quot;</li>
                          <li>
                            Add your blog domain:
                            <div className="mt-2 bg-white border border-amber-200 p-3 rounded font-mono text-xs text-stone-600">
                              https://YOUR_USERNAME.weaven.io
                            </div>
                          </li>
                          <li>
                            Make sure &quot;Allow credentials&quot; is checked
                          </li>
                        </ol>
                        <p className="mt-4 font-sans text-sm text-amber-800/60 italic">
                          This only allows Weaven to read your content. It does not
                          give Weaven ownership or write access to your data.
                        </p>
                      </div>
                      <p>
                        Without CORS configuration, you&apos;ll see an error when
                        trying to access your blog or Sanity Studio.
                      </p>
                    </div>
                  </div>

                  {/* Step 6 */}
                  <div id="setup-6">
                    <h3 className="font-sans text-lg font-medium text-ink mb-4 flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ink text-paper text-xs">
                        6
                      </span>
                      Enable Comments (Optional)
                    </h3>
                    <div className="pl-9 space-y-4 font-serif text-stone/80">
                      <p>
                        To enable the comment feature on your blog posts, you need to
                        create a Sanity API token with Editor permissions.
                      </p>
                      <div className="bg-amber-50 border border-amber-100 p-6 rounded-sm">
                        <h4 className="font-sans font-medium text-amber-900 mb-4">
                          Create Editor Token
                        </h4>
                        <ol className="list-decimal pl-5 space-y-3 font-sans text-sm text-amber-900">
                          <li>
                            Go to{" "}
                            <a
                              href="https://sanity.io/manage"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-ink underline decoration-amber-300 underline-offset-2"
                            >
                              sanity.io/manage
                            </a>
                          </li>
                          <li>Select your project &rarr; API &rarr; Tokens</li>
                          <li>Click &quot;Add API token&quot;</li>
                          <li>
                            Give it a name (e.g., &quot;Weaven Comments&quot;)
                          </li>
                          <li>
                            Set permissions to <strong>Editor</strong>
                          </li>
                          <li>Click &quot;Save&quot; and copy the token</li>
                          <li>
                            Paste the token in the &quot;Sanity Comment Token&quot;
                            field in your Weaven admin settings
                          </li>
                        </ol>
                      </div>
                      <p>
                        Without this token, visitors can read your posts but cannot
                        leave comments. The token allows Weaven to write comment data
                        to your Sanity dataset on behalf of your readers.
                      </p>
                    </div>
                  </div>

                  {/* Step 7 */}
                  <div id="setup-7">
                    <h3 className="font-sans text-lg font-medium text-ink mb-4 flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ink text-paper text-xs">
                        7
                      </span>
                      Create your blog
                    </h3>
                    <div className="pl-9 space-y-4 font-serif text-stone/80">
                      <p>
                        Once username is checked and Sanity is verified, click
                        &quot;Create Blog&quot; to complete the setup.
                      </p>
                      <p>
                        Optional: Add blog metadata like blog name, description,
                        favicon, and share image for better SEO.
                      </p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </section>

            {/* Section 5 */}
            <section id="studio">
              <FadeIn delay={0.1}>
                <h2 className="font-serif text-3xl text-ink mb-8">
                  5. Writing in Studio
                </h2>
                <div className="space-y-8">
                  <p className="font-serif text-lg text-stone/80 leading-relaxed">
                    When you open Studio through Weaven, a default schema is
                    loaded and your dataset becomes editable immediately.
                  </p>

                  <div className="bg-amber-50 border border-amber-100 p-6 rounded-sm">
                    <h4 className="font-sans font-medium text-amber-900 mb-2">
                      Important: CORS Configuration
                    </h4>
                    <p className="font-sans text-sm text-amber-800/80 mb-4 leading-relaxed">
                      To allow Weaven to read and preview your content, you must
                      explicitly allow Weaven&apos;s domain in your Sanity
                      project settings.
                    </p>

                    <ol className="list-decimal pl-5 space-y-3 font-sans text-sm text-amber-900">
                      <li>
                        Go to{" "}
                        <strong>
                          sanity.io &rarr; Your Project &rarr; API &rarr; CORS
                          Origins
                        </strong>
                      </li>
                      <li>Add the following origin:</li>
                    </ol>
                    <div className="mt-4 bg-white border border-amber-200 p-3 rounded font-mono text-xs text-stone-600">
                      https://YOUR_SUBDOMAIN.weaven.io
                    </div>
                    <p className="mt-4 font-sans text-sm text-amber-800/60 italic">
                      This setup does not give Weaven ownership of your data. It
                      only allows read access for rendering your site.
                    </p>
                  </div>

                  <div className="pl-4 border-l-2 border-stone/20">
                    <p className="font-sans text-sm text-stone uppercase tracking-widest mb-2">
                      Writing Flow
                    </p>
                    <p className="font-serif text-stone">
                      Go to{" "}
                      <code className="text-sm">
                        your-blog.weaven.io/studio
                      </code>{" "}
                      &rarr; Create post &rarr; Publish.
                    </p>
                  </div>

                  {/* How to Write a Post */}
                  <div id="studio-1" className="space-y-6">
                    <h3 className="font-sans text-lg font-medium text-ink flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-stone/20 text-ink text-xs">
                        1
                      </span>
                      How to Write a Post
                    </h3>

                    <ol className="list-decimal pl-5 space-y-4 font-sans text-base text-stone/80">
                      <li>
                        Open your Studio at{" "}
                        <code className="bg-stone/10 px-1 text-sm">
                          your-blog.weaven.io/studio
                        </code>
                      </li>
                      <li>
                        Click <strong className="text-ink">Post</strong> in the left sidebar
                      </li>
                      <li>
                        Click the <strong className="text-ink">+ Create</strong> button (or pencil icon)
                      </li>
                      <li>
                        Fill in the required fields:
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                          <li><strong className="text-ink">Title</strong> — Your post title</li>
                          <li><strong className="text-ink">Slug</strong> — URL path for your post (e.g., &quot;my-first-post&quot;)</li>
                          <li><strong className="text-ink">Content</strong> — Your post body</li>
                        </ul>
                      </li>
                      <li>
                        Click <strong className="text-ink">Publish</strong> in the bottom right
                      </li>
                    </ol>

                    <div className="bg-red-50 border border-red-100 p-6 rounded-sm">
                      <h4 className="font-sans font-medium text-red-900 mb-2">
                        Common Mistake: Missing Slug
                      </h4>
                      <p className="font-sans text-sm text-red-800/80 leading-relaxed mb-3">
                        The <strong>Slug</strong> field is required. Without it, your post URL won&apos;t work and the post won&apos;t appear on your blog.
                      </p>
                      <p className="font-sans text-sm text-red-800/80 leading-relaxed">
                        Click the <strong>&quot;Generate&quot;</strong> button next to the Slug field to auto-generate from your title, or type a custom slug using lowercase letters, numbers, and hyphens only.
                      </p>
                    </div>

                    <div className="bg-[#80d9c0]/20 border border-[#80d9c0]/40 p-6 rounded-sm">
                      <h4 className="font-sans font-medium text-ink mb-2">
                        Enabling Comments on a Post
                      </h4>
                      <p className="font-sans text-sm text-stone/80 leading-relaxed mb-3">
                        To enable comments on a specific post, you need to add <strong className="text-ink">Emojis</strong> to that post. These emojis will appear as reaction options for readers.
                      </p>
                      <p className="font-sans text-sm text-stone/80 leading-relaxed mb-3">
                        Scroll down to the <strong className="text-ink">Emojis</strong> field and add the reactions you want (e.g., ❤️ 👍 😊 🔥). Posts without emojis will not show the comment section.
                      </p>
                      <p className="font-sans text-sm text-stone/60 leading-relaxed italic">
                        Note: Make sure you&apos;ve completed Step 6 &quot;Enable Comments (Optional)&quot; with your Sanity Editor Token for comments to work properly.
                      </p>
                    </div>

                  </div>

                  {/* About Page Setup */}
                  <div id="studio-2" className="space-y-6">
                    <h3 className="font-sans text-lg font-medium text-ink flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-stone/20 text-ink text-xs">
                        2
                      </span>
                      Setting Up Your About Page
                    </h3>

                    <p className="font-serif text-stone/80 leading-relaxed">
                      Want to introduce yourself to readers? Fill in your author profile to create a personal About page.
                      <br />
                      <a
                        href="https://endvchaos.weaven.io/about"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-ink underline underline-offset-4 decoration-stone/30 hover:decoration-ink transition-colors"
                      >
                        See an example &rarr;
                      </a>
                    </p>

                    <ol className="list-decimal pl-5 space-y-4 font-sans text-base text-stone/80">
                      <li>
                        In Studio, click <strong className="text-ink">Author</strong> in the left sidebar
                      </li>
                      <li>
                        Select your author profile (or create one)
                      </li>
                      <li>
                        Fill in the fields:
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                          <li><strong className="text-ink">Name</strong> — Your display name</li>
                          <li><strong className="text-ink">Bio</strong> — A short introduction about yourself</li>
                          <li><strong className="text-ink">Image</strong> — Your profile photo (shown on each blog post)</li>
                        </ul>
                      </li>
                    </ol>

                    <div className="bg-[#80d9c0]/20 border border-[#80d9c0]/40 p-6 rounded-sm">
                      <h4 className="font-sans font-medium text-ink mb-3">
                        Show Your Interests with Group Images
                      </h4>
                      <p className="font-sans text-sm text-stone/80 leading-relaxed mb-4">
                        Add a collection of images to showcase what you&apos;re into lately — books you&apos;re reading, places you&apos;ve been, things that inspire you.
                      </p>
                      <ol className="list-decimal pl-5 space-y-2 font-sans text-sm text-stone/80">
                        <li>Scroll down to <strong className="text-ink">Group Image</strong> section</li>
                        <li>Click <strong className="text-ink">Add item</strong> to add images</li>
                        <li>
                          For each image, fill in:
                          <ul className="list-disc pl-5 mt-2 space-y-1">
                            <li><strong className="text-ink">Title</strong> — What it is (e.g., &quot;Currently Reading&quot;)</li>
                            <li><strong className="text-ink">Description</strong> — A short note about it</li>
                          </ul>
                        </li>
                      </ol>
                      <p className="font-sans text-sm text-stone/60 mt-4 italic">
                        This adds a personal touch and helps readers connect with you beyond your writing.
                      </p>
                    </div>
                  </div>

                  {/* Create Author */}
                  <div id="studio-3" className="space-y-6">
                    <h3 className="font-sans text-lg font-medium text-ink flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-stone/20 text-ink text-xs">
                        3
                      </span>
                      Create Author
                    </h3>

                    <p className="font-serif text-stone/80 leading-relaxed">
                      The Author document is required to display your name and profile on blog posts.
                      Each post can be linked to an author.
                    </p>

                    <ol className="list-decimal pl-5 space-y-4 font-sans text-base text-stone/80">
                      <li>
                        In Studio, click <strong className="text-ink">Author</strong> in the left sidebar
                      </li>
                      <li>
                        Click the <strong className="text-ink">+ Create</strong> button
                      </li>
                      <li>
                        Fill in the required fields:
                        <ul className="list-disc pl-5 mt-2 space-y-2">
                          <li><strong className="text-ink">Name</strong> — Your display name</li>
                          <li><strong className="text-ink">Slug</strong> — URL path for your author page</li>
                          <li><strong className="text-ink">Image</strong> — Your profile photo</li>
                          <li><strong className="text-ink">Bio</strong> — A short introduction (optional)</li>
                        </ul>
                      </li>
                      <li>
                        Click <strong className="text-ink">Publish</strong> to save
                      </li>
                    </ol>

                    <div className="bg-amber-50 border border-amber-100 p-6 rounded-sm">
                      <h4 className="font-sans font-medium text-amber-900 mb-2">
                        Link Author to Posts
                      </h4>
                      <p className="font-sans text-sm text-amber-800/80 leading-relaxed">
                        When creating or editing a post, select your author from the
                        <strong> Author</strong> dropdown field. This displays your name and
                        profile image on the post page.
                      </p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </section>

            {/* Section 6 */}
            <section id="collaborators">
              <FadeIn delay={0.1}>
                <h2 className="font-serif text-3xl text-ink mb-8">
                  6. Inviting Collaborators
                </h2>
                <div className="prose prose-stone font-serif text-lg leading-relaxed text-stone/80">
                  <p className="mb-6">
                    If you want to allow other people to log in to Sanity Studio with their own account,
                    you need to add them as <strong className="text-ink">members</strong> in your Sanity project.
                  </p>

                  <div className="bg-white border border-stone/20 p-6 rounded-sm not-prose">
                    <h4 className="font-sans font-medium text-ink mb-4">
                      How to Add Members
                    </h4>
                    <ol className="list-decimal pl-5 space-y-3 font-sans text-sm text-stone/80">
                      <li>
                        Go to{" "}
                        <a
                          href="https://sanity.io/manage"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-ink underline decoration-stone/30 underline-offset-2"
                        >
                          sanity.io/manage
                        </a>
                      </li>
                      <li>Select your project &rarr; Members</li>
                      <li>Click &quot;Add members&quot; and enter the email address you want to invite</li>
                      <li>Choose the appropriate role (Administrator)</li>
                    </ol>
                  </div>

                  <p className="mt-6">
                    Once added, collaborators can log in to your Studio at{" "}
                    <code className="bg-stone/10 px-1 text-sm font-sans">
                      your-blog.weaven.io/studio
                    </code>{" "}
                    using their own Sanity account.
                  </p>
                </div>
              </FadeIn>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
